import React, { useState } from "react";
import ServiceForm from "./ServiceForm";
import { CustomIcon } from "../shared/CustomIcon";
import { CoverImage } from "../shared/CoverImage";
import { formatDate } from "../../lib/format";
import { apiCreateService, apiUpdateService, apiDeleteService } from "../../lib/calendarApi";

// ============================================================
// ServicesPage — CRUD for recurring services + one-time events.
// Persisted via the kc_services storage key. Server enforces
// Authority Assignment: SA/LA may create + edit anything; Pastor /
// Team Member may edit / delete only services where their id is in
// the record's authorityIds list. Other roles see view-only.
// ============================================================
export default function ServicesPage({ currentUser, isSuperAdmin, isLimitedAdmin, services, setServices, users, addAudit, persist }) {
  const [showModal, setShowModal] = useState(false);
  const [editSvc, setEditSvc] = useState(null);
  const [prefillDate, setPrefillDate] = useState("");
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const canManage = !!(isSuperAdmin || isLimitedAdmin);
  const canEditRecord = (s) => canManage || (Array.isArray(s?.authorityIds) && s.authorityIds.includes(currentUser?.id));

  // Click-to-add from the Calendar — /admin/services?new=1&date=YYYY-MM-DD
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("new") !== "1" || !canManage) return;
    const d = url.searchParams.get("date") || "";
    setPrefillDate(/^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "");
    setEditSvc(null);
    setShowModal(true);
    url.searchParams.delete("new"); url.searchParams.delete("date");
    window.history.replaceState({}, "", `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ""}`);
  }, []);

  const saveSvc = async (data) => {
    try {
      if (data.id) {
        const fresh = await apiUpdateService(data.id, data);
        // Server has already updated kc_services — refresh local cache only.
        setServices(prev => prev.map(s => s.id === fresh.id ? fresh : s));
      } else {
        const fresh = await apiCreateService(data);
        setServices(prev => [...prev, fresh]);
        addAudit(currentUser.id, currentUser.name, "Created service", `Added ${fresh.name}`);
      }
    } catch (e) {
      alert(e.message || "Could not save service");
      return;
    }
    setShowModal(false); setEditSvc(null);
  };

  const deleteSvc = async (id) => {
    const s = services.find(x => x.id === id);
    if (!window.confirm(`Delete "${s?.name}"?`)) return;
    try {
      await apiDeleteService(id);
      setServices(prev => prev.filter(x => x.id !== id));
    } catch (e) {
      alert(e.message || "Could not delete service");
    }
  };

  return (
    <div data-testid="services-page">
      <div className="flex justify-between mb-20">
        <div style={{ fontSize: 13, color: "var(--text3)" }}>{services.length} services / events</div>
        {canManage && (
          <button className="btn btn-gold" data-testid="services-add-btn" onClick={() => { setEditSvc(null); setShowModal(true); }}>+ Add Service</button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 14 }}>
        {services.map(s => {
          const can = canEditRecord(s);
          const svcVariant = "blue";
          const badge = s.type === "recurring" ? "Recurring" : "One-time";
          return (
            <div key={s.id} className={`dept-card card-${svcVariant}`} data-testid={`services-card-${s.id}`}>
              <CoverImage
                value={s.coverImage || ""}
                title={s.name}
                subtitle={badge}
                iconName="church"
                overlay={typeof s.overlay === "number" ? s.overlay : 65}
                textColor={s.textColor || "white"}
                textEffect={s.textEffect || "shadow"}
                data-testid={`services-cover-${s.id}`}
                style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none", height: 160, aspectRatio: "auto" }}
              />
              <div className="listing-card-body">
                <div className="flex justify-between items-center">
                  <span className="text-meta">
                    {s.type === "recurring" ? `Every ${days[s.dayOfWeek || 0]}` : (s.eventDate ? formatDate(s.eventDate) : "Date TBD")}
                  </span>
                  {s.time && (
                    <span className="text-meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <CustomIcon name="clock" size={11} /> {s.time}
                    </span>
                  )}
                </div>
                {s.description && (
                  <div className="listing-card-desc">{s.description}</div>
                )}
                {!can && (
                  <div data-testid={`services-readonly-${s.id}`} className="text-meta" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <CustomIcon name="lock" size={11} /> View only
                  </div>
                )}
                <div className="flex flex-wrap" style={{ gap: 6, marginTop: 4 }}>
                  {can ? (
                    <>
                      <button className="btn-secondary btn-sm" data-testid={`services-edit-${s.id}`} onClick={() => { setEditSvc(s); setShowModal(true); }}>Edit</button>
                      <button
                        type="button"
                        data-testid={`services-delete-${s.id}`}
                        onClick={() => deleteSvc(s.id)}
                        style={{
                          background: "transparent", border: "none",
                          color: "var(--kc-red)", cursor: "pointer",
                          fontFamily: "'Inter',sans-serif", fontSize: 11,
                          fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                          padding: "6px 10px",
                        }}
                      >Delete</button>
                    </>
                  ) : (
                    <button className="btn-secondary btn-sm" data-testid={`services-view-${s.id}`} onClick={() => { setEditSvc(s); setShowModal(true); }}>View</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal cal-modal-scrollable" data-testid="services-modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{editSvc ? (canEditRecord(editSvc) ? "Edit Service" : "Service Details") : "Add Service"}</div>
              <button className="modal-close" data-testid="services-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <ServiceForm
              svc={editSvc}
              prefillDate={prefillDate}
              users={users}
              canManageAuthority={canManage}
              isSuperAdmin={!!isSuperAdmin}
              onSave={saveSvc}
              onClose={() => { setShowModal(false); setPrefillDate(""); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
